import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, User, Mail, Phone, MapPin, Calendar,
  Activity, Star, Trophy, ShieldAlert, Award
} from 'lucide-react';
import toast from 'react-hot-toast';
import { playersService } from '../services/players';
import { MEDIA_BASE_URL } from '../lib/env';
import { getFullImageUrl } from '../utils';

export default function PlayerDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [player, setPlayer] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const fetchPlayer = async () => {
    try {
      const res = await playersService.getById(id);
      const data = res?.data || res;
      setPlayer(data);
    } catch (error) {
      toast.error('Failed to load player details');
      navigate('/players');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchPlayer();
  }, [id, navigate]);

  if (isLoading) {
    return <div className="page-content"><p style={{ textAlign: 'center', marginTop: '40px' }}>Loading player profile...</p></div>;
  }

  if (!player) return null;

  const fullName = `${player.first_name || ''} ${player.middle_name || ''} ${player.last_name || ''}`.replace(/\s+/g, ' ').trim();
  const imageUrl = player.playerImage?.basePath ? getFullImageUrl(player.playerImage?.name) : '/img/default-avatar.png';
  return (
    <div className="page-content" style={{ paddingBottom: '60px' }}>

      {/* Header Navigation */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '24px' }}>
        <button
          onClick={() => navigate('/players')}
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            width: '40px', height: '40px', borderRadius: '50%',
            border: '1px solid var(--border-color)', background: 'white',
            cursor: 'pointer', transition: 'all 0.2s'
          }}
          onMouseOver={(e) => e.currentTarget.style.background = '#f1f3f5'}
          onMouseOut={(e) => e.currentTarget.style.background = 'white'}
        >
          <ArrowLeft size={20} color="var(--text-secondary)" />
        </button>
        <h1 style={{ fontSize: '24px', fontWeight: '700', color: 'var(--text-main)', margin: 0 }}>
          Player Profile
        </h1>
      </div>

      {/* Profile Cover & Header Card */}
      <div style={{
        background: 'white', borderRadius: '16px', border: '1px solid var(--border-color)',
        boxShadow: 'var(--shadow-sm)', overflow: 'hidden', marginBottom: '32px'
      }}>
        <div style={{ height: '140px', background: 'linear-gradient(135deg, var(--primary-orange), #ff8a00)' }}></div>
        <div style={{ padding: '0 32px 32px 32px', display: 'flex', flexDirection: 'column', position: 'relative' }}>

          <div style={{
            width: '120px', height: '120px', borderRadius: '50%', border: '4px solid white',
            background: '#EAECEF', marginTop: '-60px', overflow: 'hidden', flexShrink: 0,
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
          }}>
            <img
              src={imageUrl}
              alt={fullName}
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              onError={(e) => { e.target.src = '/img/default-avatar.png'; }} // assuming a default avatar exists
            />
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginTop: '16px' }}>
            <div>
              <h2 style={{ fontSize: '28px', fontWeight: '800', margin: '0 0 8px 0', color: 'var(--text-main)' }}>
                {fullName || 'Unknown Player'}
              </h2>
              <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', color: 'var(--text-secondary)', fontWeight: '500' }}>
                  <Award size={18} color="var(--primary-orange)" /> {player.role || 'Unassigned Role'}
                </span>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', color: 'var(--text-secondary)', fontWeight: '500' }}>
                  <MapPin size={18} color="var(--primary-orange)" /> {player.state || 'Unknown State'}
                </span>
                <span style={{
                  display: 'inline-flex', alignItems: 'center', padding: '4px 12px', borderRadius: '20px',
                  fontSize: '13px', fontWeight: '600', textTransform: 'capitalize',
                  background: player.status === 'active' ? '#E3FCEF' : '#FFEBE6',
                  color: player.status === 'active' ? '#006644' : '#BF2600'
                }}>
                  {player.status || 'Unknown'}
                </span>
              </div>
            </div>

            {/* Quick Stats KPIs */}
            <div style={{ display: 'flex', gap: '24px' }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '13px', color: 'var(--text-secondary)', fontWeight: '600', textTransform: 'uppercase', marginBottom: '4px' }}>Rank</div>
                <div style={{ fontSize: '24px', fontWeight: '800', color: 'var(--text-main)', display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <Star size={20} color="#FFD700" fill="#FFD700" /> {player.rank || '-'}
                </div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '13px', color: 'var(--text-secondary)', fontWeight: '600', textTransform: 'uppercase', marginBottom: '4px' }}>Matches</div>
                <div style={{ fontSize: '24px', fontWeight: '800', color: 'var(--text-main)', display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <Trophy size={20} color="var(--primary-orange)" /> {player.matches || 0}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', gap: '24px' }}>

        {/* Contact & Personal Info */}
        <div style={{ background: 'white', borderRadius: '16px', padding: '24px', border: '1px solid var(--border-color)' }}>
          <h3 style={{ fontSize: '18px', fontWeight: '700', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <User size={20} color="var(--primary-orange)" /> Personal & Contact Info
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <DetailRow icon={<Mail size={16} />} label="Email" value={player.email} />
            <DetailRow icon={<Phone size={16} />} label="Mobile" value={player.mobile_number} />
            <DetailRow icon={<Calendar size={16} />} label="Date of Birth" value={player.dob ? new Date(player.dob).toLocaleDateString() : 'N/A'} />
            <DetailRow icon={<Calendar size={16} />} label="Joining Date" value={player.joining_date ? new Date(player.joining_date).toLocaleDateString() : 'N/A'} />
            <div style={{ marginTop: '8px' }}>
              <div style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '4px' }}>Address</div>
              <div style={{ fontSize: '15px', color: 'var(--text-main)', lineHeight: '1.5' }}>
                {player.address_line_1 && <div>{player.address_line_1}</div>}
                {[player.area, player.taluka, player.district].filter(Boolean).join(', ')}
                {player.state && <div>{player.state}</div>}
                {!player.address_line_1 && !player.state && 'No address provided'}
              </div>
            </div>
          </div>
        </div>

        {/* Cricket Profile */}
        <div style={{ background: 'white', borderRadius: '16px', padding: '24px', border: '1px solid var(--border-color)' }}>
          <h3 style={{ fontSize: '18px', fontWeight: '700', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Activity size={20} color="var(--primary-orange)" /> Cricket Profile
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <DetailRow label="Primary Role" value={player.role} />
            <DetailRow label="Batting Style" value={player.batting_style} />
            <DetailRow label="Bowling Style" value={player.bowling_style} />
            <DetailRow label="Current Rank" value={player.rank} />
            <DetailRow label="Total Matches" value={player.matches} />

            {player.penalty && (
              <div style={{ marginTop: '16px', padding: '16px', borderRadius: '12px', background: '#FFF1F0', border: '1px solid #FFA39E', display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                <ShieldAlert size={20} color="#CF1322" style={{ flexShrink: 0, marginTop: '2px' }} />
                <div>
                  <div style={{ fontSize: '14px', fontWeight: '600', color: '#CF1322', marginBottom: '4px' }}>Penalty Record</div>
                  <div style={{ fontSize: '14px', color: '#A8071A', lineHeight: '1.5' }}>{player.penalty}</div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Biography & Medical */}
        <div style={{ gridColumn: '1 / -1', background: 'white', borderRadius: '16px', padding: '24px', border: '1px solid var(--border-color)' }}>
          <h3 style={{ fontSize: '18px', fontWeight: '700', marginBottom: '20px' }}>Biography & Medical</h3>

          <div style={{ marginBottom: '24px' }}>
            <div style={{ fontSize: '14px', fontWeight: '600', color: 'var(--text-secondary)', marginBottom: '8px', textTransform: 'uppercase' }}>Career Biography</div>
            <p style={{ fontSize: '15px', color: 'var(--text-main)', lineHeight: '1.6', margin: 0, whiteSpace: 'pre-wrap' }}>
              {player.biography || 'No biography provided for this player.'}
            </p>
          </div>

          <div>
            <div style={{ fontSize: '14px', fontWeight: '600', color: 'var(--text-secondary)', marginBottom: '8px', textTransform: 'uppercase' }}>Medical History</div>
            <p style={{ fontSize: '15px', color: 'var(--text-main)', lineHeight: '1.6', margin: 0, whiteSpace: 'pre-wrap' }}>
              {player.medical_history || 'No medical history reported.'}
            </p>
          </div>
        </div>

      </div>
    </div>
  );
}

// Helper component for uniform rows
function DetailRow({ icon, label, value }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--text-secondary)', fontSize: '14px' }}>
        {icon} <span>{label}</span>
      </div>
      <div style={{ fontSize: '15px', fontWeight: '500', color: 'var(--text-main)', textAlign: 'right' }}>
        {value || '-'}
      </div>
    </div>
  );
}
